import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNews } from '../components/NewsContext';
import {
  getCachedNewsBySection,
  newsCacheKeys,
} from '../services/newsCache';
import useCachedNewsQuery from './useCachedNewsQuery';
import { getCanonicalTeamName } from '../config/teamAliases';

export const useNewsFeedQuery = ({
  category,
  section,
  limitCount,
}) => {
  const { loadCategoryNews, loadSectionNews } = useNews();
  const queryType = section ? 'section' : 'category';
  const queryValue = section || category;
  const cacheKey = queryType === 'section'
    ? newsCacheKeys.section(queryValue, limitCount)
    : newsCacheKeys.category(queryValue, limitCount);
  const loadNews = useCallback(
    () => (
      queryType === 'section'
        ? loadSectionNews(queryValue, limitCount)
        : loadCategoryNews(queryValue, limitCount)
    ),
    [
      limitCount,
      loadCategoryNews,
      loadSectionNews,
      queryType,
      queryValue,
    ]
  );

  return useCachedNewsQuery(cacheKey, loadNews);
};

export const useSectionNewsBlockQuery = (section, limitCount) => {
  const { loadSectionNews } = useNews();
  const loadNews = useCallback(
    () => loadSectionNews(section, limitCount),
    [section, limitCount, loadSectionNews]
  );

  return useCachedNewsQuery(
    newsCacheKeys.section(section, limitCount),
    loadNews
  );
};

export const usePopularNewsQuery = (limitCount) => {
  const { loadPopularNews } = useNews();
  const loadNews = useCallback(
    () => loadPopularNews(limitCount),
    [limitCount, loadPopularNews]
  );

  return useCachedNewsQuery(
    newsCacheKeys.popular(limitCount),
    loadNews
  );
};

export const useRelatedNewsQuery = (currentNews, limitCount = 12) => {
  const { loadRelatedNews } = useNews();
  const loadNews = useCallback(
    () => loadRelatedNews(currentNews, limitCount),
    [currentNews, limitCount, loadRelatedNews]
  );

  return useCachedNewsQuery(
    newsCacheKeys.related(currentNews, limitCount),
    loadNews
  );
};

export const useCategoryNewsQuery = (category, limitCount) => {
  const { loadCategoryNews, getCategoryNewsCount } = useNews();
  const loadNews = useCallback(
    () => loadCategoryNews(category, limitCount),
    [category, limitCount, loadCategoryNews]
  );

  const queryResult = useCachedNewsQuery(
    newsCacheKeys.category(category, limitCount),
    loadNews
  );
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    let alive = true;

    getCategoryNewsCount(category)
      .then((count) => {
        if (alive) {
          setTotalCount(count);
        }
      })
      .catch((error) => {
        console.error('Error loading category news count:', error);
      });

    return () => {
      alive = false;
    };
  }, [category, getCategoryNewsCount]);

  return {
    ...queryResult,
    totalCount,
  };
};

export const useSectionNewsQuery = (
  section,
  limitCount,
  useContextFallback = false
) => {
  const canonicalSection = getCanonicalTeamName(section);
  const {
    newsList,
    loadSectionNews,
    getSectionNewsCount,
  } = useNews();
  const fallbackNews = useMemo(() => {
    if (!useContextFallback || !canonicalSection) {
      return [];
    }

    const providerMatches = newsList.filter((news) => (
        Array.isArray(news.sections)
        && news.sections.some(
          (value) => getCanonicalTeamName(value) === canonicalSection
        )
      ));
    const cachedMatches = getCachedNewsBySection(canonicalSection, limitCount);
    const uniqueNews = new Map();

    [...providerMatches, ...cachedMatches].forEach((news) => {
      if (news?.id && !uniqueNews.has(news.id)) {
        uniqueNews.set(news.id, news);
      }
    });

    return Array.from(uniqueNews.values()).slice(0, limitCount);
  }, [canonicalSection, limitCount, newsList, useContextFallback]);
  const loadNews = useCallback(
    () => loadSectionNews(canonicalSection, limitCount),
    [canonicalSection, limitCount, loadSectionNews]
  );

  const queryResult = useCachedNewsQuery(
    newsCacheKeys.section(canonicalSection, limitCount),
    loadNews,
    fallbackNews
  );
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    let alive = true;

    if (!queryResult.exactDataReady) {
      return undefined;
    }

    const countStart = process.env.NODE_ENV === 'development'
      ? performance.now()
      : 0;

    if (process.env.NODE_ENV === 'development') {
      console.debug(
        `[CLUB COUNT REQUEST START] ${canonicalSection} ${countStart.toFixed(1)}ms`
      );
    }

    getSectionNewsCount(canonicalSection)
      .then((count) => {
        if (alive) {
          setTotalCount(count);
        }

        if (process.env.NODE_ENV === 'development') {
          const countEnd = performance.now();
          console.debug(
            `[CLUB COUNT COMPLETE] ${canonicalSection} ${countEnd.toFixed(1)}ms `
            + `duration=${(countEnd - countStart).toFixed(1)}ms`
          );
        }
      })
      .catch((error) => {
        console.error('Error loading section news count:', error);
      });

    return () => {
      alive = false;
    };
  }, [
    getSectionNewsCount,
    queryResult.exactDataReady,
    canonicalSection,
  ]);

  return {
    ...queryResult,
    totalCount,
  };
};

export const useAllNewsQuery = (limitCount) => {
  const { loadAllNews, getAllNewsCount } = useNews();
  const loadNews = useCallback(
    () => loadAllNews(limitCount),
    [limitCount, loadAllNews]
  );

  const queryResult = useCachedNewsQuery(
    newsCacheKeys.all(limitCount),
    loadNews
  );
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    let alive = true;

    getAllNewsCount()
      .then((count) => {
        if (alive) {
          setTotalCount(count);
        }
      })
      .catch((error) => {
        console.error('Error loading all news count:', error);
      });

    return () => {
      alive = false;
    };
  }, [getAllNewsCount]);

  return {
    ...queryResult,
    totalCount,
  };
};

export const useSearchNewsQuery = (tag, limitCount) => {
  const { searchNewsByTag, getSearchNewsCount } = useNews();
  const loadNews = useCallback(
    () => searchNewsByTag(tag, limitCount),
    [tag, limitCount, searchNewsByTag]
  );

  const queryResult = useCachedNewsQuery(
    newsCacheKeys.search(tag, limitCount),
    loadNews
  );
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    let alive = true;

    if (!tag) {
      setTotalCount(0);
      return undefined;
    }

    getSearchNewsCount(tag)
      .then((count) => {
        if (alive) {
          setTotalCount(count);
        }
      })
      .catch((error) => {
        console.error('Error loading search news count:', error);
      });

    return () => {
      alive = false;
    };
  }, [tag, getSearchNewsCount]);

  return {
    ...queryResult,
    totalCount,
  };
};
